import { CommonModule, DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { TutorialPlacement, TutorialService, TutorialStep } from 'src/app/core/tutorial/tutorial.service';

interface Rect { top: number; left: number; width: number; height: number; }

@Component({
  selector: 'app-tutorial-overlay',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tutorial-overlay.component.html',
  styleUrls: ['./tutorial-overlay.component.scss']
})
export class TutorialOverlayComponent implements AfterViewInit, OnDestroy {
  @ViewChild('coachCard') coachCard?: ElementRef<HTMLElement>;
  readonly tutorial = inject(TutorialService);
  private readonly document = inject(DOCUMENT);
  private subscription = new Subscription();
  active = false;
  step: TutorialStep | null = null;
  spotlight: Rect | null = null;
  card = { top: 24, left: 24 };
  private geometryRetryTimer?: number;
  private geometryRetryAttempts = 0;

  ngAfterViewInit(): void {
    this.subscription.add(this.tutorial.active$.subscribe(active => { this.active = active; this.updateGeometry(); }));
    this.subscription.add(this.tutorial.activeStep$.subscribe(step => {
      this.step = step;
      this.geometryRetryAttempts = 0;
      window.clearTimeout(this.geometryRetryTimer);
      window.setTimeout(() => { this.updateGeometry(); this.coachCard?.nativeElement.focus(); }, 20);
    }));
  }

  ngOnDestroy(): void {
    window.clearTimeout(this.geometryRetryTimer);
    this.subscription.unsubscribe();
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  updateGeometry(): void {
    if (!this.step || !this.active) return;
    const anchor = this.document.querySelector(`[data-tutorial-anchor="${this.step.anchorId}"]`) as HTMLElement | null;
    if (!anchor) {
      this.spotlight = null;
      this.retryGeometry();
      return;
    }
    this.geometryRetryAttempts = 0;
    window.clearTimeout(this.geometryRetryTimer);
    const rect = anchor.getBoundingClientRect();
    const pad = 6;
    this.spotlight = { top: Math.max(4, rect.top - pad), left: Math.max(4, rect.left - pad), width: Math.min(window.innerWidth - 8, rect.width + pad * 2), height: Math.min(window.innerHeight - 8, rect.height + pad * 2) };
    const cardWidth = Math.min(370, window.innerWidth - 32);
    const cardHeight = this.coachCard?.nativeElement.offsetHeight ?? 270;
    const gap = 18;
    const preferred = this.positionFor(this.step.placement, rect, cardWidth, cardHeight, gap);
    this.card = {
      left: Math.max(16, Math.min(window.innerWidth - cardWidth - 16, preferred.left)),
      top: Math.max(16, Math.min(window.innerHeight - cardHeight - 16, preferred.top))
    };
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    if (!this.active) return;
    event.preventDefault();
    this.close();
  }

  next(): void { this.isLast ? this.tutorial.complete() : this.tutorial.next(); }
  previous(): void { this.tutorial.previous(); }
  close(): void { this.tutorial.skip(); }
  get index(): number { return this.step ? this.tutorial.steps.findIndex(item => item.stepId === this.step!.stepId) : 0; }
  get isFirst(): boolean { return this.index === 0; }
  get isLast(): boolean { return this.index === this.tutorial.steps.length - 1; }
  get progress(): number { return ((this.index + 1) / this.tutorial.steps.length) * 100; }
  get shadePanels(): Rect[] {
    if (!this.spotlight) return [{ top: 0, left: 0, width: window.innerWidth, height: window.innerHeight }];

    const { top, left, width, height } = this.spotlight;
    const right = left + width;
    const bottom = top + height;
    return [
      { top: 0, left: 0, width: window.innerWidth, height: top },
      { top, left: 0, width: left, height },
      { top, left: right, width: window.innerWidth - right, height },
      { top: bottom, left: 0, width: window.innerWidth, height: window.innerHeight - bottom }
    ];
  }

  private retryGeometry(): void {
    if (this.geometryRetryAttempts >= 20) return;
    this.geometryRetryAttempts += 1;
    window.clearTimeout(this.geometryRetryTimer);
    this.geometryRetryTimer = window.setTimeout(() => this.updateGeometry(), 50);
  }

  private positionFor(placement: TutorialPlacement, rect: DOMRect, width: number, height: number, gap: number): { top: number; left: number } {
    switch (placement) {
      case 'top': return { top: rect.top - height - gap, left: rect.left + rect.width / 2 - width / 2 };
      case 'right': return { top: rect.top + rect.height / 2 - height / 2, left: rect.right + gap };
      case 'left': return { top: rect.top + rect.height / 2 - height / 2, left: rect.left - width - gap };
      default: return { top: rect.bottom + gap, left: rect.left + rect.width / 2 - width / 2 };
    }
  }
}
